import React, { useState, useCallback, useMemo } from 'react';
import { 
  SupportFormData, 
  FormErrors, 
  FormTouched, 
  INITIAL_FORM_DATA, 
  INITIAL_FORM_TOUCHED
} from '../types/form';
import { 
  validateForm, 
  isFormValid,
  validateName,
  validateEmail,
  validatePaymentDate,
  validateTransactionAmount,
  validateMessage,
  validateFile
} from '../utils/validation';
import { sendSupportEmail } from '../utils/emailService';
import ErrorMessage from './ErrorMessage';

interface SupportFormProps {
  onSubmitSuccess: () => void;
}

const SupportForm: React.FC<SupportFormProps> = ({ onSubmitSuccess }) => {
  const [formData, setFormData] = useState<SupportFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<FormTouched>(INITIAL_FORM_TOUCHED);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Memoized form validation
  const formErrors = useMemo(() => validateForm(formData), [formData]);
  const formIsValid = useMemo(() => isFormValid(formErrors), [formErrors]);

  // Handle input changes
  const handleInputChange = useCallback((field: keyof SupportFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Real-time validation for touched fields
    if (touched[field]) {
      let fieldError: string | undefined;
      
      switch (field) {
      case 'name':
        fieldError = validateName(value);
        break;
      case 'email':
        fieldError = validateEmail(value);
        break;
      case 'paymentDate':
        fieldError = validatePaymentDate(value);
        break;
      case 'transactionAmount':
        fieldError = validateTransactionAmount(value);
        break;
      case 'message':
        fieldError = validateMessage(value);
        break;
      }
      
      setErrors(prev => ({
        ...prev,
        [field]: fieldError
      }));
    }
  }, [touched]);

  // Handle file upload
  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, file }));
    
    if (touched.file) {
      const fileError = validateFile(file);
      setErrors(prev => ({ ...prev, file: fileError }));
    }
  }, [touched.file]);

  // Handle field blur (mark as touched and validate)
  const handleBlur = useCallback((field: keyof FormTouched) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Validate the field when it loses focus
    let fieldError: string | undefined;
    
    switch (field) {
      case 'name':
        fieldError = validateName(formData.name);
        break;
      case 'email':
        fieldError = validateEmail(formData.email);
        break;
      case 'paymentDate':
        fieldError = validatePaymentDate(formData.paymentDate);
        break;
      case 'transactionAmount':
        fieldError = validateTransactionAmount(formData.transactionAmount);
        break;
      case 'message':
        fieldError = validateMessage(formData.message);
        break;
      case 'file':
        fieldError = validateFile(formData.file || null);
        break;
    }
    
    setErrors(prev => ({ ...prev, [field]: fieldError }));
  }, [formData]);

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!formIsValid || isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await sendSupportEmail(formData);
      onSubmitSuccess();
    } catch (error) {
      console.error('Form submission failed:', error);
      // In a real app, you'd show an error message to the user
      alert('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="support-form-container">
      <div className="form-header">
        <h2>To receive information, please fill in and send this form.</h2>
        <p>Our Support Team will contact you</p>
      </div>
      
      <form className="support-form" onSubmit={handleSubmit} noValidate>
        {/* Name Field */}
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange('name')}
            onBlur={handleBlur('name')}
            className={`form-input ${errors.name && touched.name ? 'error' : ''} ${
              !errors.name && touched.name && formData.name ? 'valid' : ''
            }`}
            placeholder="Enter your full name"
            aria-describedby={errors.name && touched.name ? 'name-error' : undefined}
            aria-invalid={errors.name && touched.name ? 'true' : 'false'}
          />
          <ErrorMessage 
            message={errors.name} 
            show={touched.name} 
          />
        </div>

        {/* Email Field */}
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            Email Address <span className="required">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            onBlur={handleBlur('email')}
            className={`form-input ${errors.email && touched.email ? 'error' : ''} ${
              !errors.email && touched.email && formData.email ? 'valid' : ''
            }`}
            placeholder="Enter your email address"
            aria-describedby={errors.email && touched.email ? 'email-error' : undefined}
            aria-invalid={errors.email && touched.email ? 'true' : 'false'}
          />
          <ErrorMessage 
            message={errors.email} 
            show={touched.email} 
          />
        </div>

        {/* Payment Date and Transaction Amount */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="paymentDate" className="form-label">
              Payment Date
            </label>
            <input
              type="date"
              id="paymentDate"
              name="paymentDate"
              value={formData.paymentDate}
              onChange={handleInputChange('paymentDate')}
              onBlur={handleBlur('paymentDate')}
              className={`form-input ${errors.paymentDate && touched.paymentDate ? 'error' : ''} ${
                !errors.paymentDate && touched.paymentDate && formData.paymentDate ? 'valid' : ''
              }`}
              max={new Date().toISOString().split('T')[0]}
              aria-describedby={errors.paymentDate && touched.paymentDate ? 'paymentDate-error' : undefined}
              aria-invalid={errors.paymentDate && touched.paymentDate ? 'true' : 'false'}
            />
            <ErrorMessage 
              message={errors.paymentDate} 
              show={touched.paymentDate} 
            />
          </div>

          <div className="form-group">
            <label htmlFor="transactionAmount" className="form-label">
              Transaction Amount
            </label>
            <input
              type="number"
              id="transactionAmount"
              name="transactionAmount"
              value={formData.transactionAmount}
              onChange={handleInputChange('transactionAmount')}
              onBlur={handleBlur('transactionAmount')}
              className={`form-input ${errors.transactionAmount && touched.transactionAmount ? 'error' : ''} ${
                !errors.transactionAmount && touched.transactionAmount && formData.transactionAmount ? 'valid' : ''
              }`}
              placeholder="0.00"
              min="0"
              step="0.01"
              aria-describedby={errors.transactionAmount && touched.transactionAmount ? 'transactionAmount-error' : undefined}
              aria-invalid={errors.transactionAmount && touched.transactionAmount ? 'true' : 'false'}
            />
            <ErrorMessage 
              message={errors.transactionAmount} 
              show={touched.transactionAmount} 
            />
          </div>
        </div>

        {/* Message Field */}
        <div className="form-group">
          <label htmlFor="message" className="form-label">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange('message')}
            onBlur={handleBlur('message')}
            className={`form-textarea ${errors.message && touched.message ? 'error' : ''} ${
              !errors.message && touched.message && formData.message ? 'valid' : ''
            }`}
            placeholder="Please describe your issue or question in detail..."
            rows={3}
            maxLength={1800}
            aria-describedby={errors.message && touched.message ? 'message-error' : undefined}
            aria-invalid={errors.message && touched.message ? 'true' : 'false'}
          />
          <ErrorMessage 
            message={errors.message} 
            show={touched.message} 
          />
        </div>

        {/* File Upload */}
        <div className="form-group">
          <label htmlFor="file" className="form-label">
            Upload File (Optional)
          </label>
          <input
            type="file"
            id="file"
            name="file"
            onChange={handleFileChange}
            onBlur={handleBlur('file')}
            className={`form-file ${errors.file && touched.file ? 'error' : ''}`}
            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
            aria-describedby={errors.file && touched.file ? 'file-error' : undefined}
            aria-invalid={errors.file && touched.file ? 'true' : 'false'}
          />
          <div className="file-help">
            Accepted formats: JPG, PNG, PDF, DOC, DOCX (max 5MB)
          </div>
          <ErrorMessage 
            message={errors.file} 
            show={touched.file} 
          />
        </div>

        {/* Submit Button */}
        <div className="form-submit">
          <button
            type="submit"
            disabled={!formIsValid || isSubmitting}
            className={`submit-button ${(!formIsValid || isSubmitting) ? 'disabled' : 'enabled'}`}
          >
            {isSubmitting ? (
              <>
                <span className="loading-spinner" />
                Sending...
              </>
            ) : (
              'Send'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupportForm;
